import { identity } from "lodash";

import Sentry from "@similarweb/sentry-logger";

import type {
    BuildNameForSelectionDeps,
    MatchingFunction,
    NameType,
    TranslatableNameWithType,
    TranslationOptions,
    Value,
} from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterNamingService";
import type FilterNamingService from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterNamingService";
import { UNKNOWN_NAME_TRANSLATABLE } from "app/pages/sales-intelligence/sub-modules/signals/services/filters/const";
import CountryService from "app/services/CountryService";

const UNKNOWN_FILTER_NAME: TranslatableNameWithType = {
    translatable: UNKNOWN_NAME_TRANSLATABLE,
};

export default function createFilterNamingService(): FilterNamingService {
    function getValueString(value: Value): string {
        return typeof value === "string" ? value : `${value}`;
    }

    function getModifiedNameKey(i18nKeyModifier: string, nameModifier: NameType): string {
        const filterI18nKey = `si.signals.filters.${i18nKeyModifier}.name`;

        return `${filterI18nKey}.${nameModifier}`;
    }

    function buildTranslationOptionsForSelection(
        values: ReadonlyArray<Value>,
        matchValueToName: MatchingFunction = identity,
    ): Readonly<TranslationOptions> {
        const [firstValue] = values;
        const text = matchValueToName(firstValue);

        if (text === undefined) {
            throw new Error(`Naming Service: no matching name found for option ${firstValue}`);
        }

        const remainingOptionsCount = values.length - 1;

        return { option: getValueString(text), remainingOptions: remainingOptionsCount };
    }

    function buildNameForBasicSelection({
        values,
        matchValueToName,
        translationOptions,
    }: BuildNameForSelectionDeps): Readonly<TranslatableNameWithType> {
        try {
            const { option, remainingOptions } = buildTranslationOptionsForSelection(values, matchValueToName);

            if (remainingOptions > 0) {
                const key = getModifiedNameKey(translationOptions.i18nKeyModifier, "partial");

                return {
                    type: "partial",
                    translatable: {
                        key: key,
                        replacements: { option, remainingOptions },
                    },
                };
            }

            return {
                type: "singular",
                translatable: {
                    key: option,
                },
            };
        } catch (error) {
            Sentry.error("Unable to create name for signal filter", error);
        }

        return UNKNOWN_FILTER_NAME;
    }

    function buildNameForCountrySelection({
        values,
        numberOfOptions,
        matchValueToName,
        translationOptions,
    }: BuildNameForSelectionDeps): Readonly<TranslatableNameWithType> {
        const isOnlyWW = values.length === 1 && CountryService.isWorldwideCountry(values[0] as number);
        if ((values.length === numberOfOptions && !isOnlyWW) || values.length === 0 || numberOfOptions === 0) {
            const key = getModifiedNameKey(translationOptions.i18nKeyModifier, "any");

            return { type: "any", translatable: { key } };
        }

        return buildNameForBasicSelection({ values, matchValueToName, numberOfOptions, translationOptions });
    }

    function buildNameForSelection({
        values,
        numberOfOptions,
        matchValueToName,
        translationOptions,
    }: BuildNameForSelectionDeps): Readonly<TranslatableNameWithType> {
        if (values.length === numberOfOptions || values.length === 0 || numberOfOptions === 0) {
            const key = getModifiedNameKey(translationOptions.i18nKeyModifier, "any");

            return { type: "any", translatable: { key } };
        }

        return buildNameForBasicSelection({ values, matchValueToName, numberOfOptions, translationOptions });
    }

    return {
        getModifiedNameKey,
        buildNameForCountrySelection,
        buildTranslationOptionsForSelection,
        buildNameForSelection,
    };
}
